/**
 * Typed client for the price manager API.
 *
 * Everything goes through one `request` so session handling and error shape are
 * uniform. Session cookies are httpOnly, so credentials: 'include' is required
 * on every call.
 */

import type { BusinessPriceRule } from '@mfs/shared';

export interface ApiError extends Error {
  status: number;
  body: any;
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set('Content-Type', 'application/json');

  const response = await fetch(path, { ...init, headers, credentials: 'include' });

  const text = await response.text();
  let body: any = text;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    /* leave as text */
  }

  if (!response.ok) {
    const error = new Error(body?.error ?? body?.message ?? `HTTP ${response.status}`) as ApiError;
    error.status = response.status;
    error.body = body;
    throw error;
  }

  return body as T;
}

// ---- Types mirroring the API responses ------------------------------------

export type UserRole = 'admin' | 'editor' | 'viewer';

export interface CurrentUser {
  id: string;
  email: string;
  name: string | null;
  role: UserRole;
  mustChangePassword: boolean;
}

export interface Channel {
  id: string;
  type: string;
  name: string;
  sortOrder: number;
  enabled: boolean;
  tokenExpiresAt: string | null;
}

/** A row of the admin channel table. Credentials are never included — only whether they work. */
export interface AdminChannel {
  id: string;
  type: string;
  name: string;
  sortOrder: number;
  enabled: boolean;
  tokenExpiresAt: string | null;
  hasCredentials: boolean;
  readiness: {
    ready: boolean;
    reason?: string;
    warning?: string;
    /** 'automatic' renews on the next sync; 'manual' needs a person. */
    renewal?: 'automatic' | 'manual';
  };
  listingCount: number;
  lastSync: { startedAt: string; finishedAt: string | null; ok: boolean; listingsSeen: number; errors: number } | null;
}

/** What a channel allows to be written. Mirrors the server's capability map. */
export interface ChannelCapabilities {
  canWritePrice: boolean;
  canWriteTitle: boolean;
  canWriteSku: boolean;
  /** Whether this channel carries a B2B price alongside the retail one — Amazon only. */
  canWriteBusinessPrice: boolean;
}

/** One channel's configured business pricing rule, with its resolved display label. */
export interface ChannelBusinessPricingRule {
  channelId: string;
  channelName: string;
  rule: BusinessPriceRule;
  label: string;
}

/** Which storefront carries a product. Drives the source tag on each row. */
export type SiteCode = 'mlf' | 'mff';

/** Whether a channel is currently showing a listing to buyers. */
export type ListingStatus = 'active' | 'inactive';

export interface Listing {
  id: string;
  channelId: string;
  channelName: string;
  channelType: string;
  externalId: string;
  sku: string | null;
  title: string;
  priceMinor: number | null;
  currency: string;
  /**
   * Amazon's business (B2B) price as of the last sync or the last post-write
   * re-read. Null means this listing carries no B2B offer — and no price
   * write will ever create one.
   */
  businessPriceMinor: number | null;
  /** A per-listing override of the channel-wide business pricing rule; null defers to it. */
  businessPriceRule: BusinessPriceRule | null;
  category: string | null;
  url: string | null;
  quantity: number | null;
  status: ListingStatus;
  linkType: string | null;
  lastSyncedAt: string | null;
  lastWriteStatus: string | null;
  /** Nothing on this listing can be written. */
  writeBlockedReason: string | null;
  /** Only the price is unwritable — title and SKU remain editable. */
  priceWriteBlockedReason: string | null;
  /** Only the SKU is unwritable — e.g. an Etsy listing with a SKU per variation. */
  skuWriteBlockedReason: string | null;
  /** Why the last write failed, when `lastWriteStatus` is 'failed'. */
  lastWriteError: string | null;
  extraFields: Record<string, string | null>;
  priceMismatch: boolean;
  /** This marketplace's SKU is not the website's — including having none at all. */
  skuMismatch: boolean;
  /** What's staged for this listing, if anything — the channel still shows the old value until synced. */
  pendingChanges: Array<{
    id: string;
    field: 'price' | 'title' | 'sku';
    newValue: string;
    status: 'queued' | 'executing' | 'conflict' | 'needs_confirmation' | 'failed';
  }>;
  /**
   * Writes pushed for this listing that haven't settled — confirmed live, or
   * reverted by a human. `in_flight` is still pushing; `awaiting_confirmation`
   * was accepted (or even confirmed once) but a background recheck is still
   * owed; `reverted_by_channel` was confirmed live and then taken back;
   * `not_confirmed` failed outright with nothing left to watch. Newest per
   * field only — an older resolved write never appears here once a newer one
   * supersedes it.
   */
  unsettledWrites: Array<{
    id: string;
    field: 'price' | 'title' | 'sku';
    status: 'pending' | 'verified' | 'failed';
    writeState: 'in_flight' | 'awaiting_confirmation' | 'reverted_by_channel' | 'not_confirmed';
    oldValue: string | null;
    newValue: string | null;
    message: string | null;
    createdAt: string;
    nextCheckAt: string | null;
    checkAttempts: number;
  }>;
}

export interface ProductRow {
  id: string;
  sku: string;
  title: string;
  imageUrl: string | null;
  primarySiteChannelId: string | null;
  /** Usually one; both when a product is carried by both storefronts. */
  sites: SiteCode[];
  hasInactiveListing: boolean;
  /** The website's own price. Context only — marketplaces are not measured against it. */
  referencePriceMinor: number | null;
  /** What the marketplaces agree on, or null when they disagree with no majority. */
  marketplacePriceMinor: number | null;
  hasPriceMismatch: boolean;
  hasSkuMismatch: boolean;
  hasWriteError: boolean;
  /** Some listing has a write pushed but not yet settled — see Listing.unsettledWrites. */
  hasUnsettledWrite: boolean;
  /**
   * Whether the Google Sheet carries a row for this SKU, as of the last full
   * sheet pull. 'unknown' means no full pull has happened yet, so nothing can
   * be claimed either way and no indicator is shown.
   */
  sheetLink: 'linked' | 'missing' | 'unknown';
  /** When a sheet row last matched this product; null if one never has. */
  sheetSeenAt: string | null;
  listings: Listing[];
}

export type ProductFilter =
  | 'all'
  | 'mismatch'
  | 'unlinked'
  | 'errors'
  | 'no_sheet'
  | 'sku_mismatch'
  | 'in_flight';

export interface WriteOutcome {
  ok: boolean;
  status: 'verified' | 'pending' | 'failed' | 'blocked' | 'needs_confirmation';
  message: string;
  editLogId?: string;
  warning?: { percentChange: number; oldValue: string; newValue: string };
}

/**
 * A change staged for a later batch push — one row in the shared queue.
 * `baselineValue` is what the listing showed when this was staged, and stays
 * frozen at that value even once a conflict is found — see `conflictValue`,
 * which is what the channel actually reports now. It's null only when the
 * field itself had no prior value to compare to.
 */
export interface PendingChangeEntry {
  id: string;
  field: 'price' | 'title' | 'sku';
  baselineValue: string | null;
  newValue: string;
  /**
   * `conflict` — the channel's own value drifted after this was staged, e.g.
   * a sync ran or someone else pushed a change. `needs_confirmation` — the
   * staged price itself crosses the large-change warning threshold and
   * nobody has confirmed it yet; nothing on the channel moved.
   */
  status: 'queued' | 'executing' | 'conflict' | 'needs_confirmation' | 'failed';
  /** Set only when status is `conflict`: what the channel actually reported at that moment — baselineValue never updates, so this is the only accurate "current" value once one's found. */
  conflictValue: string | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
  /** Null once the person who staged it has since been deleted, or when source is 'sheet'. */
  queuedBy: { email: string; name: string | null } | null;
  /** Who staged this — a person in the app, or the Google Sheet sync. */
  source: 'app' | 'sheet';
  /** Where in the sheet this came from, e.g. "Prices!row 47 (Amazon)". Null for app edits. */
  sourceRef: string | null;
  listingId: string;
  channelId: string;
  channelName: string;
  channelType: string;
  currency: string;
  listingTitle: string;
  sku: string | null;
  productTitle: string | null;
}

/**
 * The result of staging or modifying a change. A success carries the queue
 * row's id rather than a WriteOutcome's editLogId — nothing has been pushed
 * yet. A large change reports needs_confirmation in the same shape an
 * immediate write always has, so ConfirmWriteDialog needs no branching to
 * handle both.
 */
export type QueueOutcome =
  | { ok: true; id: string }
  | {
      ok: false;
      status: 'failed' | 'blocked' | 'needs_confirmation';
      message: string;
      warning?: WriteOutcome['warning'];
    };

export interface BatchItemOutcome {
  id: string;
  ok: boolean;
  status: 'executed' | 'conflict' | 'needs_confirmation' | 'failed';
  message: string;
}

export interface BatchSummary {
  executed: number;
  conflicts: number;
  needsConfirmation: number;
  failed: number;
}

/**
 * One listing's outcome from a SKU-copy action — the same vocabulary the
 * server's stageOneChange/copyWebsiteSkuForProducts use, so a skip reason from
 * either layer renders the same way.
 */
export interface StageOutcome {
  listingId: string;
  channelName: string;
  status: 'queued' | 'skipped';
  reason?: string;
  message?: string;
  warning?: WriteOutcome['warning'];
}

export interface SkuCopyProductReport {
  productId: string;
  /** The website SKU that was (or would be) copied — null when the product has none to copy. */
  sku: string | null;
  sourceChannelName: string | null;
  outcomes: StageOutcome[];
}

export interface SkuCopyResult {
  dryRun: boolean;
  queued: number;
  products: SkuCopyProductReport[];
}

export interface UnlinkedListing {
  id: string;
  channelId: string;
  channelName: string;
  externalId: string;
  sku: string | null;
  title: string;
  priceMinor: number | null;
  currency: string;
  imageUrl: string | null;
  url: string | null;
  status: ListingStatus;
  suggestions: Array<{
    productId: string;
    score: number;
    reasons: Array<{ kind: string; detail: string; points: number }>;
    product: { id: string; sku: string; skuDisplay: string; title: string; imageUrl: string | null };
  }>;
}

/**
 * A link that already exists, with anything about it that looks contradictory.
 *
 * `disagreements` is empty for the overwhelming majority — it reports positive
 * conflicts only, never the absence of evidence — so an empty list means
 * nothing contradicts, not that a human verified the link.
 */
export interface EstablishedLink {
  id: string;
  channelId: string;
  channelName: string;
  sku: string | null;
  title: string;
  priceMinor: number | null;
  currency: string;
  imageUrl: string | null;
  url: string | null;
  status: ListingStatus;
  linkType: string | null;
  product: {
    id: string;
    skuDisplay: string;
    title: string;
    imageUrl: string | null;
    sitePriceMinor: number | null;
    siteCurrency: string | null;
    siteName: string | null;
  } | null;
  disagreements: string[];
}

export interface DismissedSuggestion {
  listingId: string;
  listingTitle: string;
  listingSku: string | null;
  channelName: string;
  listingAlreadyLinked: boolean;
  productId: string;
  productSku: string;
  productTitle: string;
  score: number;
}

/**
 * Something wrong with a channel right now.
 *
 * `blocked` means it cannot sync at all, `expiring` that an authorization is
 * running out, `sync_failed` that it is configured but failing anyway, and
 * `sales_sync_failed` that its sales import keeps failing.
 */
export interface ChannelAlert {
  channelId: string;
  channelName: string;
  kind: 'expiring' | 'blocked' | 'sync_failed' | 'sales_sync_failed';
  /** Whether a person has to act, or whether it should clear itself. */
  actionRequired: boolean;
  message: string;
}

/** The password is never returned; only whether one is stored. */
export interface NotificationSettings {
  enabled: boolean;
  recipients: string[];
  fromAddress: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  hasPassword: boolean;
  warnDaysAhead: number;
}

/** One channel's override column: which price column overrides the 3rd Party default for it. */
export interface SheetOverrideColumn {
  channelId: string;
  channelName: string;
  header: string;
}

/** The private key is never returned; only whether one is stored. */
export interface SheetSettings {
  enabled: boolean;
  spreadsheetId: string;
  pricesTab: string;
  serviceAccountEmail: string;
  hasServiceAccountKey: boolean;
  overrideColumns: SheetOverrideColumn[];
  liveColumns: SheetOverrideColumn[];
  productHeader: string;
  siteHeader: string;
  syncStatusHeader: string;
  lastSyncedHeader: string;
  warnPercent: number;
  maxStagedPerRun: number;
}

export interface SheetPlanNote {
  rowNumber: number;
  skuDisplay: string;
  channelId?: string;
  kind: string;
  message: string;
}

export interface SheetSyncResult {
  ok: boolean;
  dryRun: boolean;
  rowsRead: number;
  staged: number;
  unchanged: number;
  unmatched: number;
  rejected: number;
  collisions: number;
  notes: SheetPlanNote[];
  refusedReason: string | null;
  skuAllowlistMisses: string[];
  /** The recorded run, so the tab can name what it is showing. */
  runId: string | null;
  /** The full table, so a fresh run paints without a second request. */
  detail: SheetDetail | null;
  error?: string;
}

/** One past run of the Google Sheet pull, as the History tab's "External Syncing" view reads it. */
export interface SheetSyncRun {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  rowsRead: number;
  staged: number;
  unchanged: number;
  unmatched: number;
  rejected: number;
  collisions: number;
  ok: boolean;
  /** A preview: it read the sheet and planned, but staged and wrote nothing. */
  dryRun: boolean;
  /** 'full' when the run read the whole sheet, 'partial' under a SKU allowlist. */
  sweep: string | null;
  /** Admin email for a manual run, null for the scheduled one. */
  triggeredBy: string | null;
  /** Whether this run can still be opened — detail is pruned off older runs. */
  hasDetail: boolean;
}

/**
 * The Sheet Sync tab's payload. Mirrors apps/api/src/sheets/detail.ts, which
 * carries the reasoning behind each of these shapes.
 */
export type SheetPriceRead =
  | { kind: 'blank' }
  | { kind: 'value'; minor: number }
  | { kind: 'error'; reason: string };

export type SheetCellOutcome =
  | 'stage'
  | 'refused'
  | 'blank'
  | 'no_listing'
  | 'skipped'
  | 'blocked'
  | 'currency_mismatch'
  | 'unchanged'
  | 'already_staged'
  | 'collision'
  | 'rejected';

export type SheetCellSource = 'website' | 'override' | 'third_party';

export interface SheetDetailColumn {
  channelId: string;
  channelName: string;
  /** Plain string, as everywhere else in this client — see Listing.channelType. */
  channelType: string;
  enabled: boolean;
  overrideHeader: string | null;
}

export interface SheetDetailCell {
  channelId: string;
  /**
   * The listing this cell is about; null for the placeholder standing in for a
   * channel the product has no listing on. A channel can carry two cells, since
   * a product can hold two listings on one channel.
   */
  listingId: string | null;
  outcome: SheetCellOutcome;
  currentMinor: number | null;
  targetMinor: number | null;
  from: SheetCellSource | null;
  /** Signed, so a cut reads as a cut. */
  percentChange: number | null;
  needsConfirmation: boolean;
  reason: string | null;
}

export type SheetDetailRowStatus = 'ok' | 'invalid_sku' | 'unmatched' | 'duplicate';

export interface SheetDetailRow {
  rowNumber: number;
  skuDisplay: string;
  sku: string | null;
  status: SheetDetailRowStatus;
  product: { id: string; title: string; sku: string; site: string } | null;
  notes: { kind: string; message: string }[];
  cells: SheetDetailCell[];
  read: {
    website: SheetPriceRead;
    thirdParty: SheetPriceRead;
    overrides: Record<string, SheetPriceRead>;
  };
}

export interface SheetDetail {
  version: number;
  tabName: string;
  builtAt: string;
  headers: string[];
  columns: SheetDetailColumn[];
  rows: SheetDetailRow[];
  refusedReason: string | null;
  skuAllowlistMisses: string[];
  error: string | null;
}

/** A run plus its detail, as the two detail endpoints return it. */
export interface SheetRunWithDetail extends SheetSyncRun {
  detail: SheetDetail;
}

export interface SyncResult {
  channelId: string;
  channelName: string;
  ok: boolean;
  listingsSeen: number;
  listingsNew: number;
  autoLinked: number;
  /** SKU changes on a site reconciled onto the existing product rather than forked into a new one. */
  skuRenamed?: number;
  /** SKUs that collided with a different, still-populated product — needs a human to resolve. */
  skuRenameConflicts?: string[];
  skipped?: string;
  error?: string;
  durationMs: number;
}

/** One pushed change, as the History tab reads it. */
export interface HistoryEntry {
  id: string;
  createdAt: string;
  field: string;
  oldValue: string | null;
  newValue: string | null;
  /** Only set on a "price" entry that also carried a business price — null everywhere else. */
  expectedBusinessPriceMinor: number | null;
  status: string;
  verifiedAt: string | null;
  revertedAt: string | null;
  /** Null once a user is deleted — the edit still happened. */
  user: { email: string; name: string | null } | null;
  listingId: string;
  channelId: string;
  channelName: string;
  channelType: string;
  currency: string;
  listingTitle: string;
  sku: string | null;
  productTitle: string | null;
}

/** One captured warn-or-worse line from the app's own logger. Admin > Logs. */
export interface LogEntry {
  id: string;
  level: 'warn' | 'error' | 'fatal' | string;
  category: string;
  message: string;
  context: Record<string, unknown> | null;
  createdAt: string;
}

// ---- Endpoints -------------------------------------------------------------

export const api = {
  me: () => request<CurrentUser>('/api/auth/me'),

  login: (email: string, password: string) =>
    request<CurrentUser>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  channels: () => request<Channel[]>('/api/channels'),

  /** Keyed by channel type, so a field that cannot be written is disabled up front. */
  capabilities: () => request<Record<string, ChannelCapabilities>>('/api/channels/capabilities'),

  products: (
    params: {
      search?: string;
      filter?: ProductFilter;
      status?: ListingStatus;
      site?: 'all' | SiteCode;
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.filter && params.filter !== 'all') query.set('filter', params.filter);
    if (params.status && params.status !== 'active') query.set('status', params.status);
    if (params.site && params.site !== 'all') query.set('site', params.site);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.offset !== undefined) query.set('offset', String(params.offset));
    const suffix = query.toString() ? `?${query}` : '';
    return request<{ total: number; offset: number; limit: number; products: ProductRow[] }>(
      `/api/products${suffix}`,
    );
  },

  write: (
    listingId: string,
    field: 'price' | 'title' | 'sku',
    value: number | string,
    confirmedLargeChange = false,
  ) =>
    request<WriteOutcome>(`/api/listings/${listingId}/write`, {
      method: 'POST',
      body: JSON.stringify({ field, value, confirmedLargeChange }),
    }),

  /** Every channel's configured Amazon Business pricing rule, with a display label for each. */
  businessPricing: () => request<{ rules: ChannelBusinessPricingRule[] }>('/api/business-pricing'),

  /** Sets the channel-wide business pricing rule. Admin only. */
  setChannelBusinessPricing: (channelId: string, rule: BusinessPriceRule) =>
    request<{ ok: true; rule: BusinessPriceRule; label: string }>(`/api/admin/business-pricing/${channelId}`, {
      method: 'PUT',
      body: JSON.stringify({ rule }),
    }),

  /** Sets or clears (rule: null) a per-listing override of the channel-wide business pricing rule. */
  setListingBusinessPriceRule: (listingId: string, rule: BusinessPriceRule | null) =>
    request<{ ok: true; businessPriceRule: BusinessPriceRule | null }>(
      `/api/listings/${listingId}/business-price-rule`,
      { method: 'PUT', body: JSON.stringify({ rule }) },
    ),

  /** Everything currently staged, oldest first. */
  queue: () =>
    request<{ total: number; unsettled: number; dismissable: number; entries: PendingChangeEntry[] }>(
      '/api/queue',
    ),

  /**
   * Stage a change instead of pushing it. A large change still comes back as
   * needs_confirmation, exactly like an immediate write; resubmitting with
   * confirmedLargeChange stages it for good — the batch itself never prompts.
   */
  queueChange: (
    listingId: string,
    field: 'price' | 'title' | 'sku',
    value: number | string,
    confirmedLargeChange = false,
  ) =>
    request<QueueOutcome>(`/api/listings/${listingId}/queue`, {
      method: 'POST',
      body: JSON.stringify({ field, value, confirmedLargeChange }),
    }),

  /**
   * Stages the website SKU onto every marketplace listing of this product
   * whose SKU doesn't match it. Never pushes — the report says what landed in
   * the queue and why anything else did not.
   */
  copySkuForProduct: (productId: string) =>
    request<SkuCopyResult>(`/api/products/${productId}/queue-sku-copy`, { method: 'POST' }),

  /**
   * The same, for a named set of products. `dryRun` reports what would be
   * staged without staging anything, for an accurate confirm prompt.
   */
  copySkuForProducts: (productIds: string[], dryRun = false) =>
    request<SkuCopyResult>('/api/queue/sku-copy', {
      method: 'POST',
      body: JSON.stringify({ productIds, dryRun }),
    }),

  /** Changes the staged target value without moving its baseline. */
  modifyQueuedChange: (id: string, value: number | string, confirmedLargeChange = false) =>
    request<QueueOutcome>(`/api/queue/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ value, confirmedLargeChange }),
    }),

  /** Accepts the channel's current value as the new baseline, keeping the staged target. */
  rebaselineQueuedChange: (id: string) =>
    request<{ ok: true }>(`/api/queue/${id}/rebaseline`, { method: 'POST' }),

  cancelQueuedChange: (id: string) => request<{ ok: true }>(`/api/queue/${id}`, { method: 'DELETE' }),

  clearQueue: () => request<{ ok: true; cleared: number }>('/api/queue', { method: 'DELETE' }),

  /** Confirms every row currently needing confirmation (a large price change nobody has acknowledged) in one call, putting them back in the normal queue. */
  confirmAllQueued: () => request<{ ok: true; confirmed: number }>('/api/queue/confirm-all', { method: 'POST' }),

  /** Re-evaluates every staged row's status against fresh local state — no network call, never pushes anything. */
  recheckQueue: () =>
    request<{ ok: true; rechecked: number; changed: number }>('/api/queue/recheck', { method: 'POST' }),

  /** Pushes everything staged, or just the ids named. */
  executeQueue: (ids?: string[]) =>
    request<{ outcomes: BatchItemOutcome[]; summary: BatchSummary }>('/api/queue/execute', {
      method: 'POST',
      body: JSON.stringify(ids ? { ids } : {}),
    }),

  history: (listingId: string) => request<any[]>(`/api/listings/${listingId}/history`),

  /** Every pushed change across every channel, newest first. */
  historyAll: (
    params: { channelId?: string; field?: string; outcome?: 'all' | 'problems' } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.channelId) query.set('channelId', params.channelId);
    if (params.field) query.set('field', params.field);
    if (params.outcome && params.outcome !== 'all') query.set('outcome', params.outcome);
    const suffix = query.toString() ? `?${query}` : '';
    return request<{ total: number; entries: HistoryEntry[] }>(`/api/history${suffix}`);
  },

  /** The app's own captured warn+ log lines, newest first. Admin only. */
  logs: (params: { category?: string; level?: string; limit?: number; offset?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.category) query.set('category', params.category);
    if (params.level) query.set('level', params.level);
    if (params.limit !== undefined) query.set('limit', String(params.limit));
    if (params.offset !== undefined) query.set('offset', String(params.offset));
    const suffix = query.toString() ? `?${query}` : '';
    return request<{ total: number; offset: number; limit: number; entries: LogEntry[]; categories: string[] }>(
      `/api/admin/logs${suffix}`,
    );
  },

  revert: (editLogId: string) =>
    request<WriteOutcome>(`/api/edits/${editLogId}/revert`, { method: 'POST' }),

  /** Acknowledges a write stuck at "not confirmed" or "reverted by channel" — local only, never touches the channel. */
  dismissWrite: (editLogId: string) =>
    request<{ ok: true }>(`/api/edits/${editLogId}/dismiss`, { method: 'POST' }),

  /** Bulk form of the above — every currently-dismissable write, not just one. */
  dismissAllWrites: () => request<{ ok: true; dismissed: number }>('/api/edits/dismiss-all', { method: 'POST' }),

  /**
   * Clears every staged change and every unsettled write for one product in
   * a single action — the Products page's per-row "✕". Unlike dismissWrite,
   * this is willing to give up on a write still genuinely in_flight or
   * awaiting_confirmation.
   */
  clearProductChanges: (productId: string) =>
    request<{ ok: true; clearedPending: number; clearedWrites: number }>(`/api/products/${productId}/changes`, {
      method: 'DELETE',
    }),

  /**
   * Admin-only: force-clears writes and queue rows stuck for at least
   * `olderThanMinutes`, including ones still nominally in_flight or
   * awaiting_confirmation — unlike dismissAllWrites, which only ever
   * settles a write already at a terminal state. `dryRun` reports the
   * counts without clearing anything.
   */
  forceClearStaleWrites: (olderThanMinutes: number, dryRun = false) =>
    request<
      | { ok: true; wouldClearWrites: number; wouldClearExecuting: number }
      | { ok: true; clearedWrites: number; clearedExecuting: number }
    >('/api/admin/queue/force-clear-stale', {
      method: 'POST',
      body: JSON.stringify({ olderThanMinutes, dryRun }),
    }),

  renameProduct: (productId: string, title: string) =>
    request<{ id: string; title: string }>(`/api/products/${productId}`, {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    }),

  /**
   * The review queue. Listings a channel is withholding are left out by
   * default — they are not work anyone needs to do now — and the count of what
   * was left out comes back so the UI can offer them.
   */
  unlinked: (includeInactive = false) =>
    request<{ total: number; inactiveHidden: number; listings: UnlinkedListing[] }>(
      `/api/unlinked${includeInactive ? '?includeInactive=true' : ''}`,
    ),

  /**
   * Candidate products for linking. An empty query lists everything, and
   * passing the listing keeps the results to that channel's storefront.
   *
   * Supports `term term` (all must match), `a OR b`, and `-term` to exclude.
   */
  searchProducts: (q: string, listingId?: string, site: 'auto' | 'all' = 'auto') => {
    const query = new URLSearchParams();
    if (q.trim()) query.set('q', q);
    if (listingId) query.set('listingId', listingId);
    if (site !== 'auto') query.set('site', site);
    const suffix = query.toString() ? `?${query}` : '';
    return request<{
      total: number;
      /** Which storefront the list was narrowed to, or null for everything. */
      scope: 'mlf' | 'mff' | null;
      /** 'channel' is fact; 'title' is inferred and worth saying so. */
      scopeSource: 'channel' | 'title' | null;
      products: Array<{
        id: string;
        sku: string;
        skuDisplay: string;
        title: string;
        imageUrl: string | null;
      }>;
    }>(`/api/products/search${suffix}`);
  },

  link: (listingId: string, productId: string) =>
    request<{ ok: true }>(`/api/listings/${listingId}/link`, {
      method: 'POST',
      body: JSON.stringify({ productId }),
    }),

  /**
   * Breaks the link between a listing and its product, touching neither. The
   * listing returns to the review queue to be matched again.
   */
  unlink: (listingId: string) =>
    request<{ ok: true }>(`/api/listings/${listingId}/unlink`, { method: 'POST' }),

  /**
   * Links that already exist, for auditing.
   *
   * Defaults to the ones a person decided — an exact SKU match had no room to be
   * wrong, and on the live catalog those are 306 of 331 rows.
   */
  links: (basis: 'judgement' | 'all' = 'judgement', channelId?: string) => {
    const query = new URLSearchParams({ basis });
    if (channelId) query.set('channelId', channelId);
    return request<{
      total: number;
      judgementCount: number;
      basis: 'judgement' | 'all';
      links: EstablishedLink[];
    }>(`/api/links?${query}`);
  },

  /** Suggestions a person rejected, so a mis-click is not permanent. */
  dismissedSuggestions: () =>
    request<{ total: number; dismissed: DismissedSuggestion[] }>('/api/links/dismissed'),

  restoreSuggestion: (listingId: string, productId: string) =>
    request<{ ok: true }>(`/api/listings/${listingId}/restore`, {
      method: 'POST',
      body: JSON.stringify({ productId }),
    }),

  dismissSuggestion: (listingId: string, productId: string) =>
    request<{ ok: true }>(`/api/listings/${listingId}/dismiss`, {
      method: 'POST',
      body: JSON.stringify({ productId }),
    }),

  /** Omit channelIds to sync everything; pass a subset to isolate one channel. */
  sync: (channelIds?: string[]) =>
    request<{ results: SyncResult[] }>('/api/sync', {
      method: 'POST',
      ...(channelIds ? { body: JSON.stringify({ channelIds }) } : {}),
    }),

  adminChannels: () => request<AdminChannel[]>('/api/admin/channels'),

  /**
   * Credentials are write-only: they go in and are never read back, so the form
   * that calls this always starts empty rather than showing what is stored.
   */
  setChannelCredentials: (id: string, credentials: Record<string, string>) =>
    request<{ ok: true; readiness: AdminChannel['readiness'] }>(
      `/api/admin/channels/${id}/credentials`,
      { method: 'PUT', body: JSON.stringify(credentials) },
    ),

  /** Returns the Etsy consent URL to visit; the callback stores the tokens itself. */
  etsyAuthorize: (id: string) =>
    request<{ authorizeUrl: string }>(`/api/admin/channels/${id}/etsy/authorize`, {
      method: 'POST',
    }),

  /**
   * Same for eBay. The app keyset is environment configuration rather than
   * something entered here, so consent is the only step.
   */
  ebayAuthorize: (id: string) =>
    request<{ authorizeUrl: string }>(`/api/admin/channels/${id}/ebay/authorize`, {
      method: 'POST',
    }),

  /**
   * Current channel problems. Readable by everyone, not just admins — anyone
   * looking at a price needs to know when that column stopped updating.
   */
  alerts: () => request<{ total: number; alerts: ChannelAlert[] }>('/api/alerts'),

  notificationSettings: () =>
    request<{ settings: NotificationSettings; configError: string | null }>(
      '/api/admin/notifications',
    ),

  /**
   * Partial update. Omitting smtpPassword leaves the stored one alone; sending
   * an empty string clears it.
   */
  saveNotificationSettings: (patch: Partial<NotificationSettings> & { smtpPassword?: string }) =>
    request<{ settings: NotificationSettings; configError: string | null }>(
      '/api/admin/notifications',
      { method: 'PUT', body: JSON.stringify(patch) },
    ),

  sendTestNotification: () =>
    request<{ ok: boolean; message: string }>('/api/admin/notifications/test', { method: 'POST' }),

  sendDigestNow: () =>
    request<{ ok: boolean; message: string }>('/api/admin/notifications/send-digest', {
      method: 'POST',
    }),

  sheetSettings: () =>
    request<{ settings: SheetSettings; configError: string | null }>('/api/admin/sheet'),

  saveSheetSettings: (patch: Partial<Omit<SheetSettings, 'hasServiceAccountKey'>> & { serviceAccountJson?: string }) =>
    request<{ settings: SheetSettings; configError: string | null }>('/api/admin/sheet', {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  testSheetConnection: () =>
    request<{ ok: true; headers: string[]; missing: string[] } | { ok: false; error: string }>(
      '/api/admin/sheet/test',
      { method: 'POST' },
    ),

  runSheetSync: (dryRun: boolean, skus?: string[]) =>
    request<SheetSyncResult>(`/api/sheet/sync${dryRun ? '?dryRun=1' : ''}`, {
      method: 'POST',
      body: JSON.stringify({ skus: skus && skus.length > 0 ? skus : undefined }),
    }),

  sheetHistory: (includePreviews = false) =>
    request<SheetSyncRun[]>(`/api/sheet/history${includePreviews ? '?includePreviews=1' : ''}`),

  /** The last recorded run, preview included. No Sheets API call. */
  sheetDetailLatest: () => request<{ run: SheetRunWithDetail | null }>('/api/sheet/detail/latest'),

  sheetRunDetail: (id: string) => request<{ run: SheetRunWithDetail }>(`/api/sheet/runs/${id}/detail`),

  users: () => request<any[]>('/api/users'),

  createUser: (email: string, name: string, role: UserRole) =>
    request<{ user: any; tempPassword: string }>('/api/users', {
      method: 'POST',
      body: JSON.stringify({ email, name, role }),
    }),

  updateUser: (id: string, patch: { role?: UserRole; active?: boolean; name?: string }) =>
    request<any>(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  resetUserPassword: (id: string) =>
    request<{ tempPassword: string }>(`/api/users/${id}/reset-password`, { method: 'POST' }),
};

/** Cents to a display string. Kept here so every screen formats identically. */
export function formatPrice(minor: number | null, currency = 'USD'): string {
  if (minor === null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(minor / 100);
}

/** Parses a typed price back to integer cents; null when unparseable. */
export function parsePrice(input: string): number | null {
  const cleaned = input.replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{0,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
}
